/**
 * AdminRecentTransactions.tsx
 * 
 * Reusable component for displaying recent transactions in admin dashboard.
 * Maintains exact visual design from AdminFinancials page.
 */

import React from 'react'
import { Activity } from 'lucide-react'

interface Transaction {
  id: string
  user: string
  amount: number
  type: 'subscription' | 'credit' | 'refund'
  status: 'completed' | 'pending' | 'failed'
  date: string
}

interface RecentTransactionsProps {
  transactions: Transaction[]
}

const AdminRecentTransactions: React.FC<RecentTransactionsProps> = ({ transactions }) => {
  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD'
    }).format(amount)
  }

  return (
    <div className="bg-white p-6 rounded-lg shadow-sm border border-gray-200">
      <div className="flex items-center justify-between mb-4">
        <h3 className="text-lg font-semibold text-gray-900">Recent Transactions</h3>
        <Activity className="h-5 w-5 text-gray-400" />
      </div>
      <div className="space-y-3 max-h-64 overflow-y-auto">
        {transactions.map((transaction) => (
          <div key={transaction.id} className="flex items-center justify-between p-3 bg-gray-50 rounded-md">
            <div className="flex items-center space-x-3">
              <div className={`w-2 h-2 rounded-full ${
                transaction.status === 'completed' ? 'bg-green-500' :
                transaction.status === 'pending' ? 'bg-yellow-500' : 'bg-red-500'
              }`}></div>
              <div>
                <p className="text-sm font-medium text-gray-900">{transaction.user}</p>
                <p className="text-xs text-gray-500">{transaction.type}</p>
              </div>
            </div>
            <div className="text-right">
              <p className="text-sm font-semibold text-gray-900">
                {formatCurrency(transaction.amount)}
              </p>
              <p className="text-xs text-gray-500">{transaction.date}</p>
            </div>
          </div>
        ))}
      </div>
    </div>
  )
}

export default AdminRecentTransactions 